import React, { useState } from 'react'
import Button from 'components/ds/Button'
import Icon from 'components/ds/Icon'
import Badge from 'components/ds/Badge'
import { Modal } from 'components/ds/Feedback'
import HealthcareCheckDetail from './SavedRecord/HealthcareCheckDetail'
import { ofacDetails, aliasLabel } from './SourceTabs'
import { LISTS, EXCLUSION_LISTS, hitsOn, stateName, listReason } from './screening'
import styles from './MatchTabs.module.scss'

/**
 * The drawer's results: one tab per list, exact matches first, then similar names (first 5, then
 * "Show more"), each shown as its record card. The only action on a match is "Save record"; ruling
 * everyone out is "Mark … as clear" in the drawer header.
 */

const cx = (...p) => p.filter(Boolean).join(' ')
const PAGE = 5

// The list the drawer opens on: an open exact match (OFAC first), else open similar names, else a saved
// record, else OFAC.
export const defaultList = (s) => (
    EXCLUSION_LISTS.find(k => s.lists[k].openExact.length)
    || EXCLUSION_LISTS.find(k => s.lists[k].open.length)
    || EXCLUSION_LISTS.find(k => s.lists[k].saved.length)
    || EXCLUSION_LISTS.find(k => s.lists[k].status === 'ERROR')
    || 'OFAC'
)

const MatchTabs = ({ screening, tab, onTab, decisionOf, searchedName, searchedNpi, entityName, onSave, onUndo, onRerun }) => {
    const s = screening
    const active = LISTS.find(l => l.key === tab) || LISTS[0]
    return (
        <div className={styles.root}>
            <div className={styles.tabs} role='tablist'>
                {LISTS.map(l => {
                    const info = s.lists[l.key]
                    const tone = info.openExact.length || info.saved.length ? styles.dotRed
                        : info.open.length || info.status === 'ERROR' ? styles.dotAmber : null
                    return (
                        <button key={l.key} type='button' role='tab' aria-selected={tab === l.key}
                                className={cx(styles.tab, tab === l.key && styles.tabActive)}
                                onClick={() => onTab(l.key)}>
                            {l.label}
                            {tone && <span className={cx(styles.dot, tone)} />}
                            <span className={cx(styles.tabCount, tone)}>{info.status === 'ERROR' ? '!' : info.count}</span>
                        </button>
                    )
                })}
            </div>
            <ListPanel
                key={active.key}
                list={active}
                info={s.lists[active.key]}
                searchedName={searchedName}
                searchedNpi={searchedNpi}
                entityName={entityName}
                decisionOf={decisionOf}
                onSave={onSave}
                onUndo={onUndo}
                onRerun={onRerun}
            />
        </div>
    )
}

const ListPanel = ({ list, info, searchedName, searchedNpi, entityName, decisionOf, onSave, onUndo, onRerun }) => {
    const [showAll, setShowAll] = useState(false)
    // Everything starts collapsed, one open at a time. Only an exact match still waiting for a decision
    // opens on its own; saved records and similar names stay closed until clicked.
    const [openKey, setOpenKey] = useState(() => info.openExact[0]?.matchedKey || null)
    const isDirectory = list.key === 'NPPES'

    if (info.status === 'ERROR') {
        return (
            <div className={styles.notice}>
                <strong>{list.name} didn't respond.</strong> {entityName} hasn't been checked against this list.
                {onRerun && (
                    <div><Button variant='secondary' size='small' startIcon={<Icon name='refresh-cw' size={14} />} onClick={onRerun}>Search again</Button></div>
                )}
            </div>
        )
    }
    if (info.status === 'DISABLED') return <div className={styles.muted}>{list.name} isn't turned on for your account.</div>
    if (!info.status) return <div className={styles.muted}>This list wasn't part of this search.</div>

    // One plain list, most serious first: saved records, exact matches, then similar names.
    const saved = isDirectory ? [] : info.saved
    const exact = isDirectory ? [] : info.exact.filter(c => !saved.includes(c))
    const others = info.cards.filter(c => !exact.includes(c) && !saved.includes(c))
    const ordered = [...saved, ...exact, ...others]
    const keep = Math.max(PAGE, saved.length + exact.length)
    const shown = showAll ? ordered : ordered.slice(0, keep)
    const rowProps = {
        listKey: list.key, info, searchedName, searchedNpi, entityName, decisionOf, onSave, onUndo,
        openKey, onToggle: (k) => setOpenKey(o => (o === k ? null : k)),
    }

    return (
        <div className={styles.panel}>
            {list.key === 'STATE_MEDICAID' && <MedicaidCoverage source={info.source} />}
            {isDirectory && info.cards.length > 0 && <div className={styles.muted}>Provider directory, not an exclusion list.</div>}
            {!info.cards.length && (
                <div className={styles.muted}>{isDirectory ? 'No directory record.' : `Nothing found on ${list.name}.`}</div>
            )}
            {shown.length > 0 && (
                <div className={styles.group}>
                    {shown.map(card => (
                        <CheckRecord key={card.matchedKey} card={card} exact={info.exact.includes(card)} {...rowProps} />
                    ))}
                    {ordered.length > keep && (
                        <button type='button' className={styles.link} onClick={() => setShowAll(v => !v)}>
                            {showAll ? 'Show fewer' : `Show ${ordered.length - keep} more`}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

const MedicaidCoverage = ({ source }) => {
    const covered = source?.coveredStates || []
    const missing = source?.notCoveredStates || []
    if (!covered.length && !missing.length) return null
    return (
        <div className={styles.muted}>
            {covered.length} {covered.length === 1 ? 'state' : 'states'} checked.
            {missing.length > 0 && ` No list in ${missing.length} ${missing.length === 1 ? 'state' : 'states'}: ${missing.map(stateName).join(', ')}.`}
        </div>
    )
}

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '')

// Why it came up, in a couple of words.
const whyLine = (card, listKey, exact, searchedNpi) => {
    const hit = hitsOn(card, listKey)[0] || {}
    if (listKey === 'NPPES') return [hit.npi && `NPI ${hit.npi}`, hit.providerType, hit.state].filter(Boolean).join(' · ') || 'Directory record'
    const alias = listKey === 'OFAC' && ofacDetails(hit).matchedName
        ? ` (${aliasLabel(ofacDetails(hit).matchedNameType)} ${ofacDetails(hit).matchedName})` : ''
    // Medicaid says which state's list it's on, so it's clear without opening the record.
    const where = listKey === 'STATE_MEDICAID' ? ` · ${listReason(card, listKey)}` : ''
    if (exact) return (listKey !== 'OFAC' && searchedNpi && hit.npi === searchedNpi ? 'Same NPI' : 'Same name') + alias + where
    return 'Similar name' + alias + where
}

const CheckRecord = ({ card, listKey, info, exact, searchedNpi, entityName, decisionOf, onSave, onUndo, openKey, onToggle }) => {
    const decision = decisionOf(card)
    const why = whyLine(card, listKey, exact, searchedNpi)
    const open = openKey === card.matchedKey
    // A reinstated provider (prior sanctions) is not an active exclusion, so it gets its own status.
    const marked = listKey === 'OFAC' ? 'Sanctioned' : card.status === 'PRIOR_SANCTIONS' ? 'Prior sanctions' : 'Excluded'
    const [confirming, setConfirming] = useState(false)
    const [undoing, setUndoing] = useState(false)
    const [saving, setSaving] = useState(false)
    const hit = hitsOn(card, listKey)[0] || {}
    const isDirectory = listKey === 'NPPES'
    const isSaved = decision?.decision === 'CONFIRMED'
    const isOpen = info.open.includes(card)
    // Red only for an exact match still waiting; amber for a similar name to check. A saved record is
    // already red in the status badge, so here it stays calm.
    const tone = exact && isOpen ? styles.tagRed : isOpen ? styles.tagAmber : styles.tagGrey
    const save = () => {
        setConfirming(false)
        setSaving(true)
        Promise.resolve(onSave(card)).finally(() => setSaving(false))
    }
    return (
        <div className={cx(styles.match, !isOpen && !isSaved && !isDirectory && styles.matchCalm)}>
            {/* The name toggles the record; Save record (or Record saved · Undo) sits at the top right. */}
            <div className={styles.matchHead}>
                <button type='button' className={styles.matchToggle} onClick={() => onToggle(card.matchedKey)}>
                    {!isDirectory && <span className={cx(styles.tag, tone)}>{exact ? 'Exact' : 'Similar'}</span>}
                    <span className={styles.matchText}>
                        <span className={cx(styles.matchName, exact && isOpen && styles.red)}>{hit.displayName || card.displayName}</span>
                        <span className={styles.muted}>{why}</span>
                    </span>
                </button>
                {/* NPPES can't be saved any more, but records saved before can still be undone. */}
                {isSaved && (
                    <span className={styles.matchActions}
                          title={[decision.decidedBy, fmtDay(decision.decidedAt)].filter(Boolean).join(', ')}>
                        <Badge tone='closed'>Record saved</Badge>
                        {open && <Button variant='secondary' size='small' onClick={() => setUndoing(true)}>Undo</Button>}
                    </span>
                )}
                {!isDirectory && !isSaved && open && (
                    <Button variant='primary' size='small' loading={saving} disabled={saving} onClick={() => setConfirming(true)}>
                        Save record
                    </Button>
                )}
                <button type='button' className={styles.chevron} aria-label={open ? 'Hide record' : 'Show record'}
                        onClick={() => onToggle(card.matchedKey)}>
                    <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
                </button>
            </div>
            {open && (
                <div className={styles.matchBody}>
                    <HealthcareCheckDetail card={card}
                                           decision={isSaved ? 'CONFIRMED' : decision?.decision === 'DISMISSED' ? 'DISMISSED' : 'OPEN'} />
                    {isSaved && (decision.decidedBy || decision.decidedAt) && (
                        <div className={styles.savedLine}>
                            Saved by {[decision.decidedBy, fmtDay(decision.decidedAt)].filter(Boolean).join(', ')}
                        </div>
                    )}
                </div>
            )}
            <Modal
                open={confirming}
                onClose={() => setConfirming(false)}
                title='Save this as a record?'
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setConfirming(false)}>Cancel</Button>
                        <Button variant='primary' size='small' onClick={save}>Save record</Button>
                    </>
                )}
            >
                <p>
                    {entityName} will be marked {marked} ({listReason(card, listKey)}
                    {card.status === 'PRIOR_SANCTIONS' ? ', since reinstated' : ''}).
                </p>
            </Modal>
            <Modal
                open={undoing}
                onClose={() => setUndoing(false)}
                title='Undo the saved record?'
                footer={(
                    <>
                        <Button variant='secondary' size='small' onClick={() => setUndoing(false)}>Cancel</Button>
                        <Button variant='primary' size='small' onClick={() => { setUndoing(false); onUndo([decision]) }}>Undo</Button>
                    </>
                )}
            >
                <p>
                    {isDirectory
                        ? 'The saved directory record will be removed.'
                        : `${entityName} will no longer be marked ${marked} for this match.`}
                </p>
            </Modal>
        </div>
    )
}

export default MatchTabs
